/**
 * @file ArchiveOrdersLeftDialog.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-11-03
 * @modified 2026-11-03
 * @description Asked before archiving purchase orders on their own when they
 *              are everything bought for a customer order (Cam, 3 Nov 2026 —
 *              SO-D10696 was left behind, live, with nothing bought for it).
 *              Each such order: "Archive the order too" or "Keep the order" —
 *              no default; Continue waits for every answer. Rules in
 *              utils/archiveOrdersLeft.js.
 */

import React from 'react';
import Modal from './Modal';
import { useSlideOutButton } from './SlideOutButton';
import { CHOICES, choicesComplete, orderLeftText } from '../utils/archiveOrdersLeft';
import './ArchivePOsFlow.css';

/* Mounts with the modal's portal, so its slide-outs are measured (CLAUDE.md). */
function Footer({ ready, reason, onContinue, onClose }) {
  useSlideOutButton([ready]);
  return (
    <div className="buttons slideout">
      <div>
        <button type="button" className="btn-slideout" onClick={onClose}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
          <span className="btn-label">Cancel</span>
        </button>
      </div>
      <div>
        <button type="button" className="btn-slideout btn-slideout-primary" disabled={!ready}
          title={ready ? undefined : reason} onClick={onContinue}>
          <div className="icon-wrapper"><span className="material-symbols-rounded">arrow_forward</span></div>
          <span className="btn-label">Continue</span>
        </button>
      </div>
    </div>
  );
}

/**
 * @param {Array}    orders     archive-check's orders
 * @param {object}   choices    { [order id]: 'archive' | 'keep' }
 * @param {function} onChoose   (orderId, value)
 * @param {function} onContinue
 * @param {function} onClose    Cancelled — nothing archived.
 */
function ArchiveOrdersLeftDialog({ orders, choices, onChoose, onContinue, onClose }) {
  const done = choicesComplete(orders, choices);
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={orders.length === 1 ? `${orders[0].order_number} would be left with nothing bought` : 'Orders that would be left with nothing bought'}
      size="md"
      className="archive-pos-flow archive-orders-left"
      footer={<Footer ready={done.ok} reason={done.reason} onContinue={onContinue} onClose={onClose} />}
    >
      <div className="archive-pos-body">
        {orders.map(o => (
          <div key={o.id} className="archive-orders-left-order">
            <p className="archive-orders-left-text">{orderLeftText(o)}</p>
            <div className="archive-orders-left-choices" role="radiogroup" aria-label={`What happens to ${o.order_number}`}>
              {CHOICES.map(c => {
                const on = choices[o.id] === c.value;
                const blocked = c.value === 'archive' ? o.archive_block : null;
                return (
                  <button key={c.value} type="button" role="radio" aria-checked={on}
                    className={`archive-orders-left-choice${on ? ' on' : ''}`}
                    disabled={!!blocked} title={blocked ? `Cannot archive ${o.order_number} — ${blocked}` : c.hint}
                    onClick={() => onChoose(o.id, c.value)}>
                    <span className="material-symbols-rounded" aria-hidden="true">{on ? 'radio_button_checked' : 'radio_button_unchecked'}</span>
                    {c.label}
                  </button>
                );
              })}
            </div>
            {o.archive_block && <p className="archive-pos-note">Cannot archive {o.order_number}: {o.archive_block}</p>}
            {choices[o.id] && (
              <p className="archive-orders-left-hint">{CHOICES.find(c => c.value === choices[o.id])?.hint}</p>
            )}
          </div>
        ))}
      </div>
    </Modal>
  );
}

export default ArchiveOrdersLeftDialog;
